import { cloneElement, forwardRef, memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopover } from './usePopover';
import { rectsAreEqual } from './utils';
import { useAppSelector } from '../../hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_CLIENT_RECT = {
  top: 0,
  left: 0,
  bottom: 0,
  height: 0,
  right: 0,
  width: 0
};
const PopoverPortal = ({
  target,
  popoverElement,
  children
}) => {
  useLayoutEffect(() => {
    target.appendChild(popoverElement);
    return () => {
      target.removeChild(popoverElement);
    };
  }, [target, popoverElement]);
  return /*#__PURE__*/createPortal(children, popoverElement);
};
const Popover = /*#__PURE__*/forwardRef(({
  placement,
  padding = 0,
  target = window.document.body,
  boundaryElement,
  isOpen,
  children,
  content,
  onClickOutside,
  boundaryInset = 8,
  canScrollOutOfView = true,
  zIndex,
  dismissOnClickOutside = true
}, externalRef) => {
  const prevOpen = useRef(false);
  const prevPosition = useRef(undefined);
  const childRef = useRef(undefined);
  const [popoverState, setPopoverState] = useState({
    placement,
    padding,
    childRect: EMPTY_CLIENT_RECT,
    popoverRect: EMPTY_CLIENT_RECT,
    boundaryRect: EMPTY_CLIENT_RECT,
    boundaryInset
  });
  const onPositionPopover = useCallback(newPopoverState => setPopoverState(newPopoverState), []);
  const {
    positionPopover,
    popoverRef
  } = usePopover({
    isOpen,
    childRef,
    boundaryElement,
    placement,
    padding,
    onPositionPopover,
    boundaryInset,
    canScrollOutOfView,
    zIndex
  });
  const bannersDimensions = useAppSelector(state => state.navLayoutDimensionsState.rootNav.Banners);
  const externalBannersDimensions = useAppSelector(state => state.navLayoutDimensionsState.externalNav.Banners);
  useLayoutEffect(() => {
    let shouldUpdate = true;
    const updatePopover = () => {
      if (isOpen && shouldUpdate) {
        let childRect = undefined;
        let popoverRect = undefined;
        if (childRef.current && childRef.current.getBoundingClientRect && popoverRef.current && popoverRef.current.getBoundingClientRect) {
          childRect = childRef.current.getBoundingClientRect();
          popoverRect = popoverRef.current.getBoundingClientRect();
        }
        if (childRect != null && popoverRect != null && (!rectsAreEqual(childRect, {
          top: popoverState.childRect.top,
          left: popoverState.childRect.left,
          width: popoverState.childRect.width,
          height: popoverState.childRect.height,
          bottom: popoverState.childRect.top + popoverState.childRect.height,
          right: popoverState.childRect.left + popoverState.childRect.width
        }) || popoverRect.width !== popoverState.popoverRect.width || popoverRect.height !== popoverState.popoverRect.height || popoverState.padding !== padding || placement !== prevPosition.current)) {
          positionPopover({
            popoverPosition: placement
          });
        }
        if (placement !== prevPosition.current) {
          prevPosition.current = placement;
        }
        if (shouldUpdate) {
          window.requestAnimationFrame(updatePopover);
        }
      }
      shouldUpdate = !canScrollOutOfView;
      prevOpen.current = isOpen;
    };
    window.requestAnimationFrame(updatePopover);
    return () => {
      shouldUpdate = false;
    };
  }, [isOpen, padding, popoverRef, popoverState.childRect.height, popoverState.childRect.left, popoverState.childRect.top, popoverState.childRect.width, popoverState.padding, popoverState.popoverRect.height, popoverState.popoverRect.width, positionPopover, placement, canScrollOutOfView, bannersDimensions, externalBannersDimensions]);
  const handleOnClickOutside = useCallback(e => {
    if (isOpen && popoverRef.current && popoverRef.current.contains && !popoverRef.current.contains(e.target) && childRef.current && childRef.current.contains && !childRef.current.contains(e.target) && onClickOutside) {
      onClickOutside(e);
    }
  }, [isOpen, onClickOutside, popoverRef]);
  const handleWindowResize = useCallback(() => {
    if (childRef.current) {
      window.requestAnimationFrame(() => positionPopover({
        popoverPosition: placement
      }));
    }
  }, [placement, positionPopover]);
  const handleRef = useCallback(node => {
    childRef.current = node;
    if (externalRef != null) {
      if (typeof externalRef === 'object') {
        externalRef.current = node;
      } else if (typeof externalRef === 'function') {
        externalRef(node);
      }
    }
  }, [externalRef]);
  const renderChild = () => {
    return /*#__PURE__*/cloneElement(children, {
      ref: handleRef
    });
  };
  useEffect(() => {
    if (dismissOnClickOutside) {
      window.addEventListener('click', handleOnClickOutside, true);
    }
    window.addEventListener('resize', handleWindowResize);
    return () => {
      if (dismissOnClickOutside) {
        window.removeEventListener('click', handleOnClickOutside, true);
      }
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [dismissOnClickOutside, handleOnClickOutside, handleWindowResize]);
  const renderPopover = () => {
    if (!isOpen || !content) return null;
    return /*#__PURE__*/_jsx(PopoverPortal, {
      popoverElement: popoverRef.current,
      target: target,
      children: content
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderChild(), renderPopover()]
  });
});
export default /*#__PURE__*/memo(Popover);